import { useNavigate } from 'react-router-dom'
import commonStyles from '../styles.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {Text, Button} from 'common/components'
import {
  MilestoneAction,
  MilestoneCheck,
  RequestDecline,
  useAcceptRequestStatus,
  isMilestonePassed,
  isFirstManagerActionsChecked,
  CheckActionsProps,
  MilestoneCheckProps,
} from '..'

export default function ManagerCheckActions({
  // role,
  request,
  isRequestLoading,
}: CheckActionsProps) {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const navigate = useNavigate()
  const {
    mutate: acceptRequestStatus,
    isMutating: isAcceptRequestStatusMutating,
  } = useAcceptRequestStatus(request.uuid)

  const handleStatusAccept = () => acceptRequestStatus()

  const hasAssignBorrowerCheck = request.milestones.some(
    (milestone: any) => milestone.type === 'borrower-assigned'
  )

  const hasUpdateForecastCheck = request.milestones.some(
    (milestone: any) => milestone.type === 'forecast-updated'
  )

  const isLastActionsDisabled = !isFirstManagerActionsChecked(request)

  const isStatusAcceptDisabled =
    isRequestLoading ||
    isLastActionsDisabled ||
    !isMilestonePassed('security-items-added', request) ||
    !isMilestonePassed('forecast-created', request) ||
    (hasUpdateForecastCheck &&
      !isMilestonePassed('forecast-updated', request)) ||
    (hasAssignBorrowerCheck && !isMilestonePassed('borrower-assigned', request))

  const isNotAllowed = !permissions?.includes('edit loan request (lm check)')
  const isCreateForecastNotAllowed = (!permissions?.includes('edit forecast'))

  return (
    <div className={commonStyles.checkActions}>
      <div className={commonStyles.checkActionsBlock}>
        <Text size="xs">
          <Text as="span" weight="demiBold">
            Lending manager
          </Text>
          <br />
          responsible to do:
        </Text>
        <div className={commonStyles.checkActionsMilestones}>
          <div>
            <MilestoneCheck
              type="documents-checked"
              request={request}
              disabled={isNotAllowed}
              onArrowClick={() => navigate('documents')}
            />
            <PropertiesCheck request={request} disabled={isNotAllowed} />
            <MilestoneCheck
              type="loan-summary-added"
              request={request}
              disabled={isNotAllowed}
            />
          </div>
          <div>
            <ForecastCheck
              request={request}
              disabled={
                isLastActionsDisabled || 
                isNotAllowed ||
                isCreateForecastNotAllowed
              }
            />
            <SecurtityItemsCheck
              request={request}
              disabled={isLastActionsDisabled || isNotAllowed}
            />
            {hasUpdateForecastCheck && (
              <MilestoneCheck
                type="forecast-updated"
                request={request}
                disabled={isNotAllowed}
              />
            )}
          </div>
          {hasAssignBorrowerCheck && (
            <div>
              <BorroweAssign request={request} disabled={isNotAllowed} />
            </div>
          )}
        </div>
      </div>
      { !isNotAllowed && (
        <div>
          <Button
            className={commonStyles.checkActionsButton}
            loading={isAcceptRequestStatusMutating}
            disabled={isStatusAcceptDisabled}
            onClick={handleStatusAccept}
          >
            Check has been done
          </Button>
          <RequestDecline
            requestUuid={request.uuid}
            requestStatus={request.status}
            disabled={isRequestLoading}
          />
        </div>
      )}
    </div>
  )
}

const PropertiesCheck = ({request, disabled}: MilestoneCheckProps) => {
  const setLoanOpen = useStore((state) => state.setLoanOpen)

  const handleCheck = () => setLoanOpen(true)

  return (
    <MilestoneAction
      type="properties-set"
      milestones={request.milestones}
      disabled={disabled}
      onClick={handleCheck}
    />
  )
}

const ForecastCheck = ({request, disabled}: MilestoneCheckProps) => {
  const setForecastOpen = useStore((state) => state.setForecastOpen)

  const handleCheck = () => setForecastOpen(true)

  return (
    <MilestoneAction
      type="forecast-created"
      milestones={request.milestones}
      disabled={disabled}
      onClick={handleCheck}
    />
  )
}

const SecurtityItemsCheck = ({request, disabled}: MilestoneCheckProps) => {
  const setSecurityItemsOpen = useStore((state) => state.setSecurityItemsOpen)

  const handleCheck = () => setSecurityItemsOpen(true)

  return (
    <MilestoneAction
      type="security-items-added"
      milestones={request.milestones}
      disabled={disabled}
      onClick={handleCheck}
    />
  )
}

const BorroweAssign = ({request, disabled}: MilestoneCheckProps) => {
  const setBorrowerAssignOpen = useStore((state) => state.setBorrowerAssignOpen)

  const handleCheck = () => setBorrowerAssignOpen(true)

  return (
    <MilestoneAction
      disableChecked
      type="borrower-assigned"
      milestones={request.milestones}
      disabled={disabled}
      onClick={handleCheck}
    />
  )
}
